import {
    AUTHOR_GET,
    CAMPAIGN_GET,
    CAMPAIGN_GET_LIKE,
    CAMPAIGN_GET_OVERVIEW,
    CAMPAIGN_GET_PACK,
    CAMPAIGN_GET_PARTICIPATION,
    CAMPAIGN_GET_PAYPAL,
    CAMPAIGN_GET_URL,
} from './keys';
import {
    ApiError,
    Author,
    CampaignDetails,
    CampaignOverview,
    ParticipationFilter,
} from '../types';
import {useMutation, useQuery, useQueryClient} from 'react-query';

import {CampaignService} from '../types/services/CampaignService';

export function useGetCampaign() {
    return useQuery(CAMPAIGN_GET, CampaignService.getCampaign);
}

export function useGetCampaignDetail(
    enabled: boolean,
    url: string,
    initialData: CampaignDetails | null | undefined = null
) {
    let options = {};
    if (initialData) {
        options = {
            enabled: enabled,
            initialData: initialData,
        };
    } else {
        options = {
            enabled: enabled,
            staleTime: 5000,
        };
    }
    return useQuery(
        [CAMPAIGN_GET_URL, url],
        () => CampaignService.getCampaignDetail(url),
        options
    );
}

export function useGetCampaignOverview(enabled: boolean, url: string) {
    return useQuery(
        [CAMPAIGN_GET_OVERVIEW, url],
        () => CampaignService.getCampaignOverview(url),
        {
            enabled: enabled,
        }
    );
}

export function useGetCampaignPack(enabled: boolean, url: string) {
    return useQuery([CAMPAIGN_GET_PACK, url], () => CampaignService.getCampaignPack(url), {
        enabled: enabled,
        staleTime: 5000,
    });
}

export function useGetAuthorByUrl(
    enabled: boolean,
    url: string,
    initialData: Author | null | undefined = null
) {
    let options = {};
    if (initialData) {
        options = {
            enabled: enabled,
            initialData: initialData,
        };
    } else {
        options = {
            enabled: enabled,
        };
    }

    return useQuery<Author, ApiError, Author>(
        AUTHOR_GET + ':' + url,
        () => CampaignService.getAuthorByUrl(url),
        options
    );
}

export function useGetAuthorCampaigns(enabled: boolean, authorId: string) {
    return useQuery<CampaignOverview[], ApiError, CampaignOverview[]>(
        AUTHOR_GET + ':' + authorId,
        () => CampaignService.getAuthorCampaigns(authorId),
        {
            enabled: enabled,
        }
    );
}

export function useGetCampaignLike(enabled: boolean) {
    return useQuery(CAMPAIGN_GET_LIKE, CampaignService.getCampaignLike, {
        enabled: enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

export function useLikeCampaign() {
    const queryClient = useQueryClient();
    return useMutation<number, ApiError, string>(
        (campaignUrl: string) => CampaignService.likeCampaign(campaignUrl),
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries({queryKey: [CAMPAIGN_GET_LIKE]});
            },
        }
    );
}

export function useGetCampaignParticipation(
    campaignId: string,
    page: number,
    size: number,
    enabled: boolean,
    initialData?: any
) {
    let options: any = {
        enabled: enabled,
    };
    if (initialData) {
        options.initialData = initialData;
    }
    return useQuery(
        CAMPAIGN_GET_PARTICIPATION + ':' + campaignId + ':' + page,
        () =>
            CampaignService.getCampaignParticipation(
                {
                    campaign_id: campaignId,
                    order: 'DESC',
                    comment_only: true,
                } as ParticipationFilter,
                page,
                size
            ),
        options
    );
}

export async function fetchCampaignParticipation(
    campaignId: string,
    order: string,
    commentOnly: boolean,
    page: number,
    size: number
) {
    return await CampaignService.getCampaignParticipation(
        {
            campaign_id: campaignId,
            order: order,
            comment_only: commentOnly,
        } as ParticipationFilter,
        page,
        size
    );
}

export async function fetchCampaignParticipationServer(
    campaignId: string,
    serverUrl: string,
    order: string = 'DESC',
    commentOnly: boolean = true,
    page: number = 1,
    size: number = 50
) {
    const url = serverUrl + '/campaign/participation?page=' + page + '&size=' + size;
    const requestOptions = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            campaign_id: campaignId,
            order: order,
            comment_only: commentOnly,
        }),
    };
    const result = await fetch(url, requestOptions);
    return await result.json();
}

export function useCampaignPaypal(campaignUrl: string) {
    return useQuery(CAMPAIGN_GET_PAYPAL + ':' + campaignUrl, () =>
        CampaignService.getCampaignPaypal(campaignUrl)
    );
}
